// Suppress known floating third-party widgets (chat launchers) while
// the QuoteSense overlay is open, and restore them exactly on close.
//
// Why a curated selector list instead of a generic "hide anything
// fixed at the bottom-right" heuristic: a heuristic risks hiding
// legitimate merchant UI. Shopify Inbox (live on The Sensory Supply)
// is the priority target — it renders at max int z-index in its own
// stacking context, so out-z-indexing it isn't possible; hiding it
// for the overlay's duration is the only robust fix.
//
// Fully reversible + defensive: every DOM read/write is wrapped so a
// hostile theme (odd getters, frozen style objects, etc.) can never
// throw out of suppress/restore, and a matched element's prior
// `display` is always stashed before it's hidden so restore is exact.

const FLOATING_WIDGET_SELECTORS = [
  // Shopify Inbox — priority target (live on TSS storefront)
  "#shopify-chat",
  ".shopify-chat",
  '[id^="shopify-chat"]',
  'iframe[src*="shopify" i][src*="chat" i]',
  // Call-now floating button — confirmed second overlapper on TSS storefront
  "#call-now-btn-floating-wrapper",
  // Intercom
  ".intercom-lightweight-app",
  "#intercom-container",
  ".intercom-launcher",
  // Tidio
  "#tidio-chat",
  // Gorgias
  "#gorgias-chat-container",
  "#gorgias-chat-messenger-icon",
  // Crisp
  ".crisp-client",
  // Zendesk
  "#launcher",
  "iframe#launcher",
  // Facebook Messenger
  ".fb_dialog",
  ".fb-customerchat",
];

const SUPPRESSED_MARKER = "data-qb-widget-suppressed";
const PRIOR_DISPLAY_ATTR = "data-qb-widget-prior-display";

let suppressedEls: HTMLElement[] = [];

/** Hide every element matching the curated selector list, stashing
 *  each one's prior inline `display` so restore is exact. Idempotent
 *  — a second call while already suppressed is a no-op per element
 *  (it won't re-stash "none" over the real prior value). */
export function suppressFloatingWidgets(root: ParentNode = document): void {
  try {
    for (const selector of FLOATING_WIDGET_SELECTORS) {
      let matches: NodeListOf<Element>;
      try {
        matches = root.querySelectorAll(selector);
      } catch {
        continue; // malformed/unsupported selector in this environment — skip it
      }
      matches.forEach((node) => {
        const elNode = node as HTMLElement;
        try {
          if (elNode.hasAttribute(SUPPRESSED_MARKER)) return; // already suppressed
          elNode.setAttribute(PRIOR_DISPLAY_ATTR, elNode.style.display || "");
          elNode.setAttribute(SUPPRESSED_MARKER, "1");
          elNode.style.setProperty("display", "none", "important");
          suppressedEls.push(elNode);
        } catch {
          /* never let one hostile element block the rest */
        }
      });
    }
  } catch {
    /* never throw out of the overlay's open path */
  }
}

/** Restore every element previously hidden by suppressFloatingWidgets
 *  to its exact prior inline `display`. Safe to call multiple times
 *  or when nothing was suppressed. */
export function restoreFloatingWidgets(): void {
  try {
    for (const elNode of suppressedEls) {
      try {
        const prior = elNode.getAttribute(PRIOR_DISPLAY_ATTR) || "";
        if (prior) elNode.style.display = prior;
        else elNode.style.removeProperty("display");
        elNode.removeAttribute(PRIOR_DISPLAY_ATTR);
        elNode.removeAttribute(SUPPRESSED_MARKER);
      } catch {
        /* never strand teardown on one bad element — keep restoring the rest */
      }
    }
  } catch {
    /* never throw out of the overlay's close path */
  } finally {
    suppressedEls = [];
  }
}
